import { StrictMode, useEffect } from 'react'
import { Switch, Route, useLocation, Router } from 'wouter'
import './index.css'
import { SWRConfig } from 'swr'
import { fetcher } from './lib/fetcher'
import { Toaster } from '@/components/ui/toaster'

import { ErrorBoundary } from './components/ErrorBoundary'
import { LoadingProvider } from './components/LoadingProvider'
import NotFound from './components/NotFound'
import { MobileProvider } from './hooks/use-mobile'
import WalletPage from './pages/WalletPage'
import OrderPage from './pages/OrderPage'
import OrderHistoryPage from './pages/OrderHistoryPage'
import PaymentMethodsPage from './pages/account/PaymentMethodsPage'
import LoginPage from './pages/LoginPage'
import SignupPage from './pages/SignupPage'
import TransactionsPage from './pages/TransactionsPage'
import WaiverPage from './pages/WaiverPage'
import MembershipPage from './pages/MembershipPage'
import EntryPassesPage from './pages/EntryPassesPage'
import ActiveEntryPassPage from './pages/ActiveEntryPassPage'
import AppKitProvider from './providers/AppKitProvider'
import { UserProvider } from './providers/UserProvider'
import { getFirebaseApp, isRoleEnough, UserRole } from './constants'
import { useUser } from './hooks/use-user'
import AdminLayout from './components/pos/admin/admin-layout'
import AdminLoginPage from './pages/admin/AdminLoginPage'
import { PosAuthProvider } from './hooks/pos/use-pos-auth'
import AuthActionsPage from './pages/AuthActionsPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import ResendVerificationPage from './pages/ResendVerificationPage'
import TermsPage from './pages/TermsPage'
import PrivacyPolicyPage from './pages/PrivacyPolicyPage'
import { getAuth } from 'firebase/auth'
import BuyTokensPage from './pages/BuyTokensPage.tsx'
import BanxaCheckoutPage from './pages/BanxaCheckoutPage.tsx'
import EventsPage from './pages/EventsPage.tsx'
import NewsPage from './pages/NewsPage.tsx'
import GalleriesPage from './pages/GalleriesPage.tsx'
import ShowEventPage from './pages/events/ShowPage.tsx'
import ShowNewsPage from './pages/news/ShowPage.tsx'
import ShowGalleryPage from './pages/galleries/ShowPage.tsx'
import AccountPage from './pages/AccountPage.tsx'
import EditProfilePage from './pages/account/EditProfilePage.tsx'
import ChangeUsernamePage from './pages/account/ChangeUsernamePage.tsx'
import ChangeEmailPage from './pages/account/ChangeEmailPage.tsx'
import ChangePasswordPage from './pages/account/ChangePasswordPage.tsx'
import TwoFactorAuthPage from './pages/account/TwoFactorAuthPage.tsx'
import LinkedWalletsPage from './pages/account/LinkedWalletsPage.tsx'
import DeleteAccountPage from './pages/account/DeleteAccountPage.tsx'
import ChatPage from './pages/ChatPage.tsx'
import NavigationTabs from './components/common/NavigationTabs.tsx'
import { useNavigationTabs } from './hooks/use-navigation-tabs.ts'
import { PosCartGuardProvider } from './providers/PosCartGuardProvider.tsx'
import { NotificationProvider } from './providers/NotificationProvider.tsx'
import { WebSocketProvider } from './providers/WebSocketProvider.tsx'
import { DeviceIntegrityProvider } from './providers/DeviceIntegrityProvider.tsx'
import NotificationsPage from './pages/NotificationsPage.tsx'
import CabanasPage from './pages/CabanasPage.tsx'
import CabanaDayPage from './pages/reservations/DayPage.tsx'
import CabanaNightPage from './pages/reservations/NightPage.tsx'
import CabanaClubPage from './pages/reservations/ClubPage.tsx'

// Enhanced navigation guard with improved initial load handling
function withNavigationGuard(
	Component: React.ComponentType<any>,
	requiredRole: UserRole = UserRole.USER
) {
	return function GuardedComponent(props: any) {
		const [location, navigate] = useLocation()

		const user = useUser()
		const auth = getAuth(getFirebaseApp())

		useEffect(() => {
			if(user.loading) {
				return
			}

			if(user.id && !isRoleEnough(user.role, requiredRole)) {
				navigate('/', { replace: true })
			}

			if(!auth?.currentUser?.uid) {
				console.log('--->>>> Redirect back to login from Page guard')
				navigate('/login')
			}
		}, [user, location])

		// Don't render anything while redirecting unauthenticated users
		if (!user.loading && !auth?.currentUser?.uid) {
			return null
		}

		// Don't render if user lacks the required role
		if (!user.loading && user.id && !isRoleEnough(user.role, requiredRole)) {
			return null
		}

		return (
			<ErrorBoundary>
				<Component {...props} />
			</ErrorBoundary>
		)
	}
}

// Guard all pages
const guardedPages = {
	WalletPage: withNavigationGuard(WalletPage),
	OrderPage: withNavigationGuard(OrderPage),
	TransactionsPage: withNavigationGuard(TransactionsPage),
	BuyTokensPage: withNavigationGuard(BuyTokensPage),
	WaiverPage: withNavigationGuard(WaiverPage),
	MembershipPage: withNavigationGuard(MembershipPage),
	EntryPassesPage: withNavigationGuard(EntryPassesPage),
	ActiveEntryPassPage: withNavigationGuard(ActiveEntryPassPage),
	BanxaCheckoutPage: withNavigationGuard(BanxaCheckoutPage),
	EventsPage: withNavigationGuard(EventsPage),
	NewsPage: withNavigationGuard(NewsPage),
	GalleriesPage: withNavigationGuard(GalleriesPage),
	ShowEventPage: withNavigationGuard(ShowEventPage),
	ShowNewsPage: withNavigationGuard(ShowNewsPage),
	ShowGalleryPage: withNavigationGuard(ShowGalleryPage),
	AccountPage: withNavigationGuard(AccountPage),
	NotificationsPage: withNavigationGuard(NotificationsPage),
	ChatPage: withNavigationGuard(ChatPage),
	CabanasPage: withNavigationGuard(CabanasPage),
	CabanaDayPage: withNavigationGuard(CabanaDayPage),
	CabanaNightPage: withNavigationGuard(CabanaNightPage),
	CabanaClubPage: withNavigationGuard(CabanaClubPage),
	OrderHistoryPage: withNavigationGuard(OrderHistoryPage),
	PaymentMethodsPage: withNavigationGuard(PaymentMethodsPage),
	EditProfilePage: withNavigationGuard(EditProfilePage),
	ChangeUsernamePage: withNavigationGuard(ChangeUsernamePage),
	ChangeEmailPage: withNavigationGuard(ChangeEmailPage),
	ChangePasswordPage: withNavigationGuard(ChangePasswordPage),
	TwoFactorAuthPage: withNavigationGuard(TwoFactorAuthPage),
	LinkedWalletsPage: withNavigationGuard(LinkedWalletsPage),
	DeleteAccountPage: withNavigationGuard(DeleteAccountPage)
}

// Bridge: provides WebSocketProvider with Firebase user credentials
function CustomerWSBridge({ children }: { children: React.ReactNode }) {
	const { uid, id } = useUser()
	return (
		<WebSocketProvider userId={uid || id} userRole="customer">
			{children}
		</WebSocketProvider>
	)
}

// Enhanced app initialization
const App = () => {
	const { isVisible } = useNavigationTabs()

	return (
	<StrictMode>
		<Router>
			<Switch>
				<Route path="/change-email-confirm">
					<ErrorBoundary>
						<AuthActionsPage />
					</ErrorBoundary>
				</Route>
				<Route path="/reset-password">
					<ErrorBoundary>
						<AuthActionsPage />
					</ErrorBoundary>
				</Route>
				<Route path="/verify-email">
					<ErrorBoundary>
						<AuthActionsPage />
					</ErrorBoundary>
				</Route>

				{/* Admin routes — independent auth, no Firebase */}
				<Route path="/admin/login">
					<PosAuthProvider>
						<AdminLoginPage />
						<Toaster />
					</PosAuthProvider>
				</Route>
				<Route path="/admin" component={AdminLayout} />
				<Route path="/admin/*" component={AdminLayout} />

				<Route>
					<ErrorBoundary>
						<AppKitProvider>
							<MobileProvider>
								<LoadingProvider>
									<UserProvider>
									<CustomerWSBridge>
									<NotificationProvider>
										<DeviceIntegrityProvider>
										<PosCartGuardProvider>
											<SWRConfig
												value={{
													fetcher,
													revalidateOnFocus: false,
													revalidateIfStale: false,
													dedupingInterval: 3000,
													errorRetryCount: 2,
													loadingTimeout: 5000,
													onError: (error) => {
														console.error('SWR Error:', error)
													},
													shouldRetryOnError: false
												}}>
												<Switch>
													<Route path="/" component={guardedPages.WalletPage}/>

													<Route path="/order/history" component={guardedPages.OrderHistoryPage}/>
												<Route path="/order" component={guardedPages.OrderPage}/>
													<Route path="/login" component={LoginPage}/>
													<Route path="/signup" component={SignupPage}/>
													<Route path="/terms-of-use">
														<TermsPage redirectBackUrl={"/account"} />
													</Route>

													<Route
														path="/privacy-policy"
														component={PrivacyPolicyPage}
													/>
													<Route
														path="/forgot-password"
														component={ForgotPasswordPage}
													/>
													<Route
														path="/resend-verification"
														component={ResendVerificationPage}
													/>
													<Route
														path="/transactions"
														component={guardedPages.TransactionsPage}
													/>
														<Route
														path="/buy-tokens"
														component={guardedPages.BuyTokensPage}
													/>
													<Route
														path="/banxa-checkout"
														component={guardedPages.BanxaCheckoutPage}
													/>
													<Route
														path="/waivers"
														component={guardedPages.WaiverPage}
													/>
													<Route
														path="/membership"
														component={guardedPages.MembershipPage}
													/>
													<Route
														path="/entry-pass"
														component={guardedPages.ActiveEntryPassPage}
													/>
													<Route
														path="/entry-passes"
														component={guardedPages.EntryPassesPage}
													/>
													<Route
														path="/reservations/day"
														component={guardedPages.CabanaDayPage}
													/>
													<Route
														path="/reservations/night"
														component={guardedPages.CabanaNightPage}
													/>
													<Route
														path="/reservations/club"
														component={guardedPages.CabanaClubPage}
													/>
	<Route
														path="/reservations"
														component={guardedPages.CabanasPage}
													/>
													<Route
														path="/events/:id"
														component={guardedPages.ShowEventPage}
													/>
													<Route
														path="/events"
														component={guardedPages.EventsPage}
													/>
													<Route
														path="/news/:id"
														component={guardedPages.ShowNewsPage}
													/>
													<Route
														path="/news"
														component={guardedPages.NewsPage}
													/>
													<Route
														path="/galleries/:id"
														component={guardedPages.ShowGalleryPage}
													/>
													<Route
														path="/galleries"
														component={guardedPages.GalleriesPage}
													/>
													<Route
														path="/account/payment-methods"
														component={guardedPages.PaymentMethodsPage}
													/>
													<Route
														path="/account/edit-profile"
														component={guardedPages.EditProfilePage}
													/>
													<Route
														path="/account/change-username"
														component={guardedPages.ChangeUsernamePage}
													/>
													<Route
														path="/account/change-email"
														component={guardedPages.ChangeEmailPage}
													/>
													<Route
														path="/account/change-password"
														component={guardedPages.ChangePasswordPage}
													/>
													<Route
														path="/account/two-factor-auth"
														component={guardedPages.TwoFactorAuthPage}
													/>
													<Route
														path="/account/linked-wallets"
														component={guardedPages.LinkedWalletsPage}
													/>
													<Route
														path="/account/delete-account"
														component={guardedPages.DeleteAccountPage}
													/>
													<Route
														path="/account"
														component={guardedPages.AccountPage}
													/>
													<Route
														path="/notifications"
														component={guardedPages.NotificationsPage}
													/>
													<Route
														path="/chat"
														component={guardedPages.ChatPage}
													/>
													<Route component={NotFound}/>
												</Switch>
												{isVisible && <NavigationTabs />}
												<Toaster/>
											</SWRConfig>
										</PosCartGuardProvider>
										</DeviceIntegrityProvider>
									</NotificationProvider>
									</CustomerWSBridge>
									</UserProvider>
								</LoadingProvider>
							</MobileProvider>
						</AppKitProvider>
					</ErrorBoundary>
				</Route>
			</Switch>
		</Router>
	</StrictMode>
	)
}

export default App